import { useId, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { RootState } from "state/rootReducer";
import { LoadingSpinner } from "components/LoadingSpinner/LoadingSpinner";
import Modal from "components/Modal";
import { ICON_STROKE, ImportIcon } from "shared/icons";

interface OwnProps {
  isImporting: boolean;
  onImport: (playlist: FeaturedPlaylistResult) => void;
  onCancel: () => void;
}

interface StateProps {
  userPlaylists: FeaturedPlaylistResult[] | null;
}

interface DispatchProps {}

export type ImportPlaylistModalProps = OwnProps &
  StateProps &
  ResolveThunks<DispatchProps>;

function ImportPlaylistModal(props: ImportPlaylistModalProps): JSX.Element {
  const [selectedPlaylist, setSelectedPlaylist] = useState<string | null>(null);
  const groupName = useId();

  console.log(`Found ${props.userPlaylists?.length} playlists`);

  // Import was previously enabled with nothing selected, which passed
  // `undefined` to onImport. Only one playlist can be chosen, so these are
  // radios rather than checkboxes — and the button waits for a choice.
  const canImport = selectedPlaylist !== null && !props.isImporting;

  return (
    <Modal
      title="Import a Spotify playlist"
      subtitle="Every track in the playlist you choose is added as a seed."
      onCancel={props.onCancel}
      footer={
        <>
          <button
            type="button"
            className="button-generic"
            onClick={() => props.onCancel()}
          >
            Cancel
          </button>
          {/* A Spotify action, so it takes Spotify green. */}
          <button
            disabled={!canImport}
            type="button"
            className="button-spotify flex items-center gap-2"
            onClick={() =>
              props.onImport(
                props.userPlaylists!.find((p) => p.id === selectedPlaylist)!
              )
            }
          >
            <ImportIcon
              size={16}
              strokeWidth={ICON_STROKE}
              aria-hidden="true"
            />
            {props.isImporting ? "Importing…" : "Import playlist"}
          </button>
        </>
      }
    >
      {props.userPlaylists === null ? (
        <LoadingSpinner border={false} />
      ) : props.userPlaylists.length === 0 ? (
        <p className="text-sm text-ink-2 m-0">
          No playlists found on your Spotify account.
        </p>
      ) : (
        <div
          role="radiogroup"
          aria-label="Your playlists"
          className="flex flex-col gap-1"
        >
          {props.userPlaylists.map((p) => {
            const checked = selectedPlaylist === p.id;

            return (
              <label
                key={p.id}
                className={`flex items-center gap-3 px-3 py-2.5 rounded border cursor-pointer transition-colors ${
                  checked
                    ? "border-accent bg-accent-dim"
                    : "border-transparent hover:bg-row-hover"
                }`}
              >
                <input
                  type="radio"
                  name={groupName}
                  className="w-4 h-4 flex-none"
                  checked={checked}
                  onChange={() => setSelectedPlaylist(p.id)}
                />
                <span className="text-sm text-ink truncate">{p.name}</span>
              </label>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

function mapState(rootState: RootState): StateProps {
  return {
    userPlaylists: rootState.user.userPlaylists,
  };
}

const mapDispatch: DispatchProps = {};

export default connect(mapState, mapDispatch)(ImportPlaylistModal);
